import { Button, Icon, Popover } from '@sgme/ui';
import { DocumentError } from 'apiConnector/document/document';
import { ErrorDisplayContext } from 'components/shared/Document/MultiLanguageDocumentItem';
import { WorkflowErrorDetailsListLegacy } from 'components/shared/WorkflowError/WorkflowErrorDetailsListLegacy';
import { WorkflowErrorSummary } from 'components/shared/WorkflowError/WorkflowErrorSummary';
import { DocumentFormat } from 'epic/common-form/Documentation';
import { useTranslation } from 'hooks/useTranslation';
import { useState } from 'react';
import Card from 'react-bootstrap/Card';

export interface MultiLanguageDocumentErrorProps {
  error: DocumentError;
  errorContext?: ErrorDisplayContext;
  e2e?: string;
  lang: string;
  documentFormat: DocumentFormat;
  id?: string;
  initOpen?: boolean;
}

export function MultiLanguageDocumentError({
  error,
  errorContext,
  e2e,
  lang,
  documentFormat,
  id,
  initOpen = false,
}: MultiLanguageDocumentErrorProps) {
  const [isOpen, setIsOpen] = useState(initOpen);

  return (
    <Popover
      color="danger"
      maxWidth={620}
      style={{ padding: 0 }}
      placement="top"
      contents={
        <MultiLanguageDocumentErrorContent error={error} errorContext={errorContext} onClose={() => setIsOpen(false)} />
      }
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Button iconPosition="icon-only" purpose="link" data-e2e={`${e2e}-error-icon-${lang}-${documentFormat}`} id={id}>
        <Icon size="sm" icon="block" className="m-0 text-danger" />
      </Button>
    </Popover>
  );
}

type MultiLanguageDocumentErrorContentProps = Pick<MultiLanguageDocumentErrorProps, 'error' | 'errorContext'> & {
  onClose: () => void;
};

export function MultiLanguageDocumentErrorContent({
  onClose,
  error,
  errorContext,
}: MultiLanguageDocumentErrorContentProps) {
  const errorDetailsErrorTranslation = useTranslation('errorDetails');
  const downloadTermsheetErrorMessage = useTranslation('downloadTermsheet.error');
  const downloadTermsheetErrorMessageForExternal = useTranslation('downloadTermsheet.functional.error');

  const totalNumberOfErrors =
    error.type === 'REJECTION' && errorContext?.shouldDisplayErrorDetails ? error.rejections.length : undefined;

  return (
    <Card className="text-primary shadow text-start border border-danger">
      <Card.Header className="text-danger d-flex justify-content-between align-items-center">
        <h6 className="text-nowrap">
          <span className="align-middle">
            <span className="me-2">{errorDetailsErrorTranslation}</span>
            {Boolean(totalNumberOfErrors) && <span>{`(${totalNumberOfErrors})`}</span>}
          </span>
        </h6>
        <Button
          size="sm"
          color="danger"
          purpose="flat"
          data-e2e="error-details-close-button"
          iconPosition="icon-only"
          onClick={onClose}
          className="ms-2"
        >
          <Icon icon="close" className="m-0" />
        </Button>
      </Card.Header>
      <Card.Body>
        {error.type === 'TECHNICAL' && (
          <div data-e2e="errors-panel-technical-error" className="me-2 mb-2 text-danger">
            {downloadTermsheetErrorMessage}
          </div>
        )}

        {error.type === 'END_USER_ERROR' && (
          <ul className="my-3">
            {error.errorMessages.map((msg, index) => (
              <li key={index} className="text-primary">
                {msg}
              </li>
            ))}
          </ul>
        )}

        {error.type === 'REJECTION' && errorContext?.shouldDisplayErrorDetails && (
          <WorkflowErrorDetailsListLegacy workflowErrors={error.rejections} />
        )}

        {error.type === 'REJECTION' && !errorContext?.shouldDisplayErrorDetails && (
          <>
            <div data-e2e="errors-panel-technical-error" className="me-2 mb-2 text-danger">
              {downloadTermsheetErrorMessageForExternal}
            </div>
            <WorkflowErrorSummary
              productSubtype={errorContext?.productSubtype}
              shouldUseRejectionCode={errorContext?.shouldUseRejectionCode ?? false}
              workflowErrors={error.rejections}
            />
          </>
        )}
      </Card.Body>
    </Card>
  );
}
